import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { IS_NATIVE_BUILD } from "../native/platform";
import { errorText } from "../ui";
import { HelpText } from "./AiAssistant";
import "./hh-ai.css";

/**
 * (Public assistant) "Senvia 助手" for visitors who are NOT signed in — sign-in / sign-up pages of the web app (the
 * marketing site has its own vanilla copy in www.js). Pre-sales questions about the Senvia platform only (the server
 * refuses anything else). No account, nothing stored on the server: the last turns go along with each question.
 * "Contact a person" = e-mail support@senviapos.com (tickets need an account).
 */
interface Status { available: boolean; remainingToday: number; dailyLimit: number; maxLength: number }
type Msg = { role: "user"; text: string } | { role: "assistant"; text: string; links?: Array<{ id: string; title: string; url: string }> } | { role: "error"; text: string };
export const SUPPORT_EMAIL = "support@senviapos.com";

export function PublicSupportAssistant() {
  const { me } = useAuth();
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [human, setHuman] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const shown = !me && !IS_NATIVE_BUILD;
  const lang = locale === "zh" ? "zh" : "en";

  const load = useCallback(async () => { try { setStatus(await api<Status>("/api/public/assistant/status")); } catch { setStatus(null); } }, []);
  useEffect(() => { if (shown && open && !status) void load(); }, [shown, open, status, load]);
  useEffect(() => { endRef.current?.scrollIntoView?.({ block: "end" }); }, [msgs, busy, human]);
  if (!shown) return null;
  const available = !!status?.available;

  const send = async (textArg?: string) => {
    const text = (textArg ?? input).trim().slice(0, 500);
    if (!text || busy || !available) return;
    const history = msgs.filter((m): m is Exclude<Msg, { role: "error" }> => m.role !== "error").slice(-6).map((m) => ({ role: m.role, content: m.text.slice(0, m.role === "user" ? 500 : 1500) }));
    setInput(""); setMsgs((m) => [...m, { role: "user", text }]); setBusy(true);
    try {
      const r = await api<{ reply: string; links: Array<{ id: string; title: string; url: string }>; remainingToday: number }>("/api/public/assistant/chat", { method: "POST", body: { message: text, lang, history } });
      setMsgs((m) => [...m, { role: "assistant", text: r.reply, links: r.links }]);
      setStatus((s) => (s ? { ...s, remainingToday: r.remainingToday } : s));
    } catch (e) { setMsgs((m) => [...m, { role: "error", text: errorText(e) }]); } finally { setBusy(false); }
  };

  return (
    <>
      <button type="button" className="ai-fab hh-fab pa-fab" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="public-assistant-fab">💬 {t("hh.sa.button")}</button>
      {open && (
        <aside className="ai-panel hh-support" role="dialog" aria-label={t("hh.sa.button")} data-testid="public-assistant">
          <header className="ai-head">
            <strong>💬 {t("hh.sa.button")}</strong>
            <span className="spacer" />
            <button className="btn btn-quiet btn-sm" onClick={() => { setMsgs([]); setHuman(false); }}>{t("hh.sa.new")}</button>
            <button className="btn btn-quiet btn-sm" aria-label={t("hh.sa.close")} onClick={() => setOpen(false)}>✕</button>
          </header>
          <div className="ai-body">
            {status && !available ? <div className="note">{t("pa.unavailable", { email: SUPPORT_EMAIL })}</div> : (<>
              <p className="muted small">{t("pa.intro")}</p>
              {msgs.length === 0 && (
                <div className="hh-suggest">
                  {(["pa.suggest1", "pa.suggest2", "pa.suggest3"] as const).map((k) => <button key={k} type="button" className="chip" onClick={() => void send(t(k))} disabled={!available}>{t(k)}</button>)}
                </div>
              )}
            </>)}
            {msgs.map((m, i) => (
              <div key={i} className={"ai-msg ai-" + (m.role === "error" ? "assistant" : m.role)}>
                {m.role === "user" && <div className="ai-bubble">{m.text}</div>}
                {m.role === "error" && <div className="ai-bubble hh-error">{m.text}</div>}
                {m.role === "assistant" && (<>
                  <div className="ai-bubble"><HelpText text={m.text.replace(/\s*\[[a-z0-9-]{2,40}\]/g, "")} /></div>
                  {!!m.links?.length && (
                    <div className="row ai-links">
                      {m.links.slice(0, 3).map((l) => <a key={l.id} className="btn btn-quiet btn-sm" href={l.url} target="_blank" rel="noopener">{t("hh.sa.open")} → {l.title}</a>)}
                    </div>
                  )}
                </>)}
              </div>
            ))}
            {busy && <div className="muted">{t("hh.sa.thinking")}</div>}
            {human && <div className="note" data-testid="public-assistant-human">{t("pa.human", { email: SUPPORT_EMAIL })} <a href={"mailto:" + SUPPORT_EMAIL}>{SUPPORT_EMAIL}</a></div>}
            <div ref={endRef} />
          </div>
          <div className="hh-foot">
            <button type="button" className="btn btn-quiet btn-sm" onClick={() => setHuman((h) => !h)}>🙋 {t("hh.sa.human")}</button>
            {available && status && <span className="muted small">{t("hh.sa.remaining", { n: status.remainingToday })}</span>}
          </div>
          <form className="ai-input" onSubmit={(e) => { e.preventDefault(); void send(); }}>
            <textarea rows={2} value={input} maxLength={500} placeholder={t("pa.placeholder")} onChange={(e) => setInput(e.target.value)} disabled={!available}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }} />
            <button className="btn btn-primary" type="submit" disabled={busy || !input.trim() || !available}>{t("hh.sa.send")}</button>
          </form>
        </aside>
      )}
    </>
  );
}
